import { isAxiosError } from "axios";
import { httpClient } from "@/services/http/client";
import { resolveApiBaseUrl } from "@/config/api-base-url";
import type { AuthUser } from "@/stores/authStore";

type LoginResponse = {
  data: {
    accessToken: string;
    expiresIn: number;
    user: AuthUser;
  };
};

export type SessionResult = LoginResponse["data"];

/** H3: one of the student's signed-in devices (no IP addresses are ever sent). */
export type SignedInDevice = {
  id: string;
  browser: string | null;
  os: string | null;
  signedInAt: string;
  lastActiveAt: string;
  place: string | null;
};

/**
 * H3 device limit: the password (and code) were right, but the student is already signed in on
 * `deviceLimit` devices. Nothing was created; one device must be signed out first.
 */
export type DeviceLimitStep = {
  kind: "device_limit";
  deviceChoiceToken: string;
  expiresAt: string;
  deviceLimit: number;
  devices: SignedInDevice[];
};

type DeviceLimitData = Omit<DeviceLimitStep, "kind"> & { deviceLimitReached: true };

function isDeviceLimitData(data: unknown): data is DeviceLimitData {
  return Boolean(
    data &&
    typeof data === "object" &&
    (data as { deviceLimitReached?: unknown }).deviceLimitReached,
  );
}

function deviceLimitStep(data: DeviceLimitData): DeviceLimitStep {
  return {
    kind: "device_limit",
    deviceChoiceToken: data.deviceChoiceToken,
    expiresAt: data.expiresAt,
    deviceLimit: data.deviceLimit,
    devices: data.devices,
  };
}

/** True when a sign-in step answered with the device choice instead of a session. */
export function isDeviceLimitStep(step: unknown): step is DeviceLimitStep {
  return Boolean(
    step && typeof step === "object" && (step as { kind?: unknown }).kind === "device_limit",
  );
}

/** What a code step (or the device choice) leads to: a session, or (H3) the device choice. */
export type SignInResult = SessionResult | DeviceLimitStep;

async function signInStep(path: string, body: object): Promise<SignInResult> {
  const res = await httpClient.post<{ data: SessionResult | DeviceLimitData }>(path, body);
  const data = res.data.data;
  return isDeviceLimitData(data) ? deviceLimitStep(data) : data;
}

/** What the password step leads to (AUTH-003 / AUTH-008). */
export type LoginStep =
  | ({ kind: "session" } & SessionResult)
  | {
      kind: "otp";
      challengeToken: string;
      maskedEmail: string;
      /** The server would honour "remember this device" (`AUTH_TRUSTED_DEVICES=on`); off by default. */
      rememberDeviceOffered?: boolean;
    }
  | { kind: "verify_email"; challengeToken: string; maskedEmail: string }
  | DeviceLimitStep;

type ApiErrorBody = {
  error?: { code?: string; message?: string; challengeToken?: string; maskedEmail?: string };
};

export async function loginWithPassword(input: {
  email: string;
  password: string;
  tenantSlug: string;
}): Promise<LoginStep> {
  try {
    const res = await httpClient.post<{
      data:
        | SessionResult
        | DeviceLimitData
        | {
            otpRequired: true;
            challengeToken: string;
            maskedEmail: string;
            rememberDeviceOffered?: boolean;
          };
    }>("/auth/login", input);
    const data = res.data.data;
    if (isDeviceLimitData(data)) return deviceLimitStep(data);
    if ("otpRequired" in data) {
      return {
        kind: "otp",
        challengeToken: data.challengeToken,
        maskedEmail: data.maskedEmail,
        rememberDeviceOffered: data.rememberDeviceOffered === true,
      };
    }
    return { kind: "session", ...data };
  } catch (err) {
    const body = isAxiosError<ApiErrorBody>(err) ? err.response?.data?.error : undefined;
    if (body?.code === "EMAIL_NOT_VERIFIED" && body.challengeToken) {
      return {
        kind: "verify_email",
        challengeToken: body.challengeToken,
        maskedEmail: body.maskedEmail ?? "",
      };
    }
    throw err;
  }
}

export async function verifyLoginOtp(input: {
  challengeToken: string;
  code: string;
  rememberDevice: boolean;
}): Promise<SignInResult> {
  return signInStep("/auth/login/verify-otp", input);
}

export async function resendLoginOtp(challengeToken: string): Promise<void> {
  await httpClient.post("/auth/login/resend-otp", { challengeToken });
}

export async function verifyEmailOtp(input: {
  challengeToken: string;
  code: string;
}): Promise<SessionResult> {
  const res = await httpClient.post<LoginResponse>("/auth/verify-email-otp", input);
  return res.data.data;
}

/** H3: the sign-in page's email code (an unverified address): a session or the device choice. */
export async function verifyEmailOtpStep(input: {
  challengeToken: string;
  code: string;
}): Promise<SignInResult> {
  return signInStep("/auth/verify-email-otp", input);
}

/** H3: sign the chosen device out, then finish this sign-in. */
export async function chooseDeviceToSignOut(input: {
  deviceChoiceToken: string;
  signOutDeviceId: string;
}): Promise<SignInResult> {
  return signInStep("/auth/login/device-choice", input);
}

/** H3: give up this sign-in; the token is spent and nothing is created. */
export async function cancelDeviceChoice(deviceChoiceToken: string): Promise<void> {
  await httpClient.post("/auth/login/device-choice/cancel", { deviceChoiceToken });
}

export async function resendEmailOtp(challengeToken: string): Promise<void> {
  await httpClient.post("/auth/verify-email-otp/resend", { challengeToken });
}

export async function fetchLoginRedirect(): Promise<{ redirectTo: string }> {
  const res = await httpClient.get<{ data: { redirectTo: string } }>("/v1/auth/login-redirect");
  return res.data.data;
}

export async function logoutSession(): Promise<void> {
  await httpClient.post("/auth/logout");
}

export async function registerAccount(input: {
  name: string;
  email: string;
  password: string;
  tenantSlug: string;
  /** §31: a staff invite being redeemed (lets a new staff member into an approved-only school). */
  inviteToken?: string;
  /** §31: optional note kept on the join request when the email isn't approved. */
  message?: string;
  /** H4: the required "I agree to the Terms of use and Privacy notice" checkbox. */
  acceptTerms: true;
}): Promise<{
  challengeToken: string | null;
  maskedEmail: string | null;
  /** §31: the school is approved-only and this email isn't listed — a join request was sent. */
  joinRequested?: boolean;
}> {
  const res = await httpClient.post<{
    data: { challengeToken?: string | null; maskedEmail?: string | null; status?: string };
  }>("/v1/auth/register", input);
  if (res.data.data?.status === "join_requested") {
    return { challengeToken: null, maskedEmail: null, joinRequested: true };
  }
  return {
    challengeToken: res.data.data?.challengeToken ?? null,
    maskedEmail: res.data.data?.maskedEmail ?? null,
  };
}

export async function requestPasswordReset(input: {
  email: string;
  tenantSlug: string;
}): Promise<void> {
  await httpClient.post("/v1/auth/forgot-password", input);
}

export async function resetPassword(input: {
  token: string;
  password: string;
  tenantSlug: string;
}): Promise<void> {
  await httpClient.post("/v1/auth/reset-password", input);
}

export async function setOAuthTenant(tenantSlug: string): Promise<void> {
  await httpClient.post("/v1/auth/oauth-tenant", { tenantSlug });
}

export async function completeOAuthSession(): Promise<LoginResponse["data"]> {
  const res = await httpClient.post<LoginResponse>("/auth/oauth-complete", {});
  return res.data.data;
}

export function googleSignInUrl(callbackPath = "/auth/post-login"): string {
  const callbackUrl = `${window.location.origin}${callbackPath}`;
  const base = resolveApiBaseUrl();
  return `${base}/auth/signin/google?callbackUrl=${encodeURIComponent(callbackUrl)}`;
}
